import type { CaseEvent } from '@core/domain/model';
import { failure, guard, type DataResult } from '@core/domain/result';
import { newId, toEvent } from './mappers';
import type { Database } from './sqlite';

export function eventRepository(db: Database, now: () => number = Date.now) {
  const find = async (id: string) =>
    (await db.query('SELECT * FROM events WHERE id = ?', [id]))[0] ?? null;

  return {
    async ofCase(caseId: string): Promise<CaseEvent[]> {
      const rows = await db.query(
        'SELECT * FROM events WHERE caseId = ? AND deletedAt IS NULL ORDER BY startAt',
        [caseId],
      );
      return rows.map(toEvent);
    },

    /**
     * Невыполненные события в интервале — то, что показывает строка дела.
     *
     * Выполненные не возвращаются намеренно: под делом должно стоять то, что ещё
     * предстоит. Сделанная вчера задача в роли «ближайшего события» вводит в заблуждение.
     */
    async between(from: number, to: number): Promise<CaseEvent[]> {
      const rows = await db.query(
        `SELECT * FROM events
          WHERE deletedAt IS NULL AND isCompleted = 0
            AND COALESCE(deadlineAt, startAt) BETWEEN ? AND ?
          ORDER BY COALESCE(deadlineAt, startAt)`,
        [from, to],
      );
      return rows.map(toEvent);
    },

    async overdue(at: number): Promise<CaseEvent[]> {
      const rows = await db.query(
        `SELECT * FROM events
          WHERE deletedAt IS NULL AND isCompleted = 0
            AND COALESCE(deadlineAt, startAt) < ?
          ORDER BY COALESCE(deadlineAt, startAt)`,
        [at],
      );
      return rows.map(toEvent);
    },

    async all(): Promise<CaseEvent[]> {
      const rows = await db.query(
        'SELECT * FROM events WHERE deletedAt IS NULL ORDER BY COALESCE(deadlineAt, startAt)',
      );
      return rows.map(toEvent);
    },

    async find(id: string): Promise<CaseEvent | null> {
      const row = await find(id);
      return row && row.deletedAt === null ? toEvent(row) : null;
    },

    async create(draft: Omit<CaseEvent, 'createdAt' | 'updatedAt'> & { id?: string }) {
      return guard(async () => {
        const id = draft.id?.trim() ? draft.id : newId();
        const at = now();

        await db.run(
          `INSERT INTO events (id, title, type, startAt, endAt, description, caseId, clientId,
                               reminderMinutesBefore, isCompleted, deadlineAt,
                               createdAt, updatedAt, deletedAt)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, NULL)`,
          [
            id,
            draft.title,
            draft.type,
            draft.startAt,
            draft.endAt,
            draft.description,
            draft.caseId,
            draft.clientId,
            draft.reminderMinutesBefore,
            draft.isCompleted ? 1 : 0,
            draft.deadlineAt,
            at,
            at,
          ],
        );
        return id;
      });
    },

    async update(value: CaseEvent): Promise<DataResult<void>> {
      if (!(await find(value.id))) return failure({ kind: 'NotFound', id: value.id });

      return guard(async () => {
        await db.run(
          `UPDATE events SET title = ?, type = ?, startAt = ?, endAt = ?, description = ?,
                             caseId = ?, clientId = ?, reminderMinutesBefore = ?,
                             isCompleted = ?, deadlineAt = ?, updatedAt = ?
            WHERE id = ?`,
          [
            value.title,
            value.type,
            value.startAt,
            value.endAt,
            value.description,
            value.caseId,
            value.clientId,
            value.reminderMinutesBefore,
            value.isCompleted ? 1 : 0,
            value.deadlineAt,
            now(),
            value.id,
          ],
        );
      });
    },

    async setCompleted(id: string, isCompleted: boolean): Promise<DataResult<void>> {
      if (!(await find(id))) return failure({ kind: 'NotFound', id });

      return guard(async () => {
        await db.run('UPDATE events SET isCompleted = ?, updatedAt = ? WHERE id = ?', [
          isCompleted ? 1 : 0,
          now(),
          id,
        ]);
      });
    },

    async remove(id: string): Promise<DataResult<void>> {
      if (!(await find(id))) return failure({ kind: 'NotFound', id });

      return guard(async () => {
        const at = now();
        await db.run('UPDATE events SET deletedAt = ?, updatedAt = ? WHERE id = ?', [at, at, id]);
      });
    },

    async restore(id: string): Promise<DataResult<void>> {
      if (!(await find(id))) return failure({ kind: 'NotFound', id });

      return guard(async () => {
        await db.run('UPDATE events SET deletedAt = NULL, updatedAt = ? WHERE id = ?', [
          now(),
          id,
        ]);
      });
    },
  };
}

export type EventRepository = ReturnType<typeof eventRepository>;
